import { gameAssetResources } from '../../assets/GameAssetResources';
import { assetKey, type AssetDescriptor, type AssetLocation } from '../../assets/AssetContracts';
import { assertLocationValid } from '../../assets/AssetLoader';

export interface AudioBufferLoadOptions {
  readonly cache?: boolean;
  readonly signal?: AbortSignal;
  readonly descriptor?: AssetDescriptor;
  readonly location?: AssetLocation;
}

interface DecodedBufferEntry {
  readonly promise: Promise<AudioBuffer>;
  byteLength: number;
}

const DECODED_AUDIO_BUDGET_BYTES = 64 * 1024 * 1024;

export class AudioRuntime {
  #context: AudioContext | null = null;
  readonly #buffers = new Map<string, DecodedBufferEntry>();
  #decodedBytes = 0;
  readonly #lifetime = new AbortController();
  #disposed = false;

  get context(): AudioContext {
    if (this.#disposed) throw new Error('Audio runtime is disposed.');
    if (!this.#context) this.#context = new AudioContext({ latencyHint: 'interactive' });
    return this.#context;
  }

  get existingContext(): AudioContext | null {
    return this.#context;
  }

  async unlock(): Promise<void> {
    const context = this.context;
    if (context.state === 'suspended') await context.resume();
  }

  loadBuffer(url: string, options: AudioBufferLoadOptions = {}): Promise<AudioBuffer> {
    if (this.#disposed) return Promise.reject(new Error('Audio runtime is disposed.'));
    if (options.location) assertLocationValid(options.location);
    const immutable = options.descriptor?.access.kind === 'public'
      || /(?:^|\/)api\/media\/revisions\/[^/?#]+$/.test(url)
      || /(?:^|\/)releases\/[^/]+\/assets\//.test(url);
    if (!immutable || options.cache === false || options.signal || options.descriptor?.access.kind === 'private') {
      return this.#fetchAndDecode(url, options);
    }
    const key = options.descriptor ? assetKey(options.descriptor) : url;
    const cached = this.#buffers.get(key);
    if (cached) {
      this.#buffers.delete(key);
      this.#buffers.set(key, cached);
      return cached.promise;
    }

    const entry: DecodedBufferEntry = { byteLength: 0, promise: this.#fetchAndDecode(url, options).then(buffer => {
      if (this.#buffers.get(key) === entry) {
        entry.byteLength = buffer.length * buffer.numberOfChannels * Float32Array.BYTES_PER_ELEMENT;
        this.#decodedBytes += entry.byteLength;
        this.#trimBuffers();
      }
      return buffer;
    }).catch(error => {
      if (this.#buffers.get(key) === entry) this.#buffers.delete(key);
      throw error;
    }) };
    this.#buffers.set(key, entry);
    this.#trimBuffers();
    return entry.promise;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#lifetime.abort();
    this.#buffers.clear();
    this.#decodedBytes = 0;
    void this.#context?.close().catch(() => undefined);
    this.#context = null;
  }

  #trimBuffers(): void {
    while (this.#buffers.size > 64 || this.#decodedBytes > DECODED_AUDIO_BUDGET_BYTES) {
      const oldest = this.#buffers.keys().next().value!;
      this.#decodedBytes -= this.#buffers.get(oldest)!.byteLength;
      this.#buffers.delete(oldest);
    }
  }

  async #fetchAndDecode(url: string, options: AudioBufferLoadOptions): Promise<AudioBuffer> {
    const context = this.context;
    const signal = AbortSignal.any([this.#lifetime.signal, ...(options.signal ? [options.signal] : [])]);
    const lease = options.descriptor
      ? await gameAssetResources.loader.acquire(options.descriptor, { resolve: async () => options.location ?? { url } }, { signal })
      : await gameAssetResources.acquire(url, { signal });
    try {
      signal.throwIfAborted();
      const decoded = await context.decodeAudioData(await lease.readBytes());
      signal.throwIfAborted();
      if (options.location) assertLocationValid(options.location);
      return decoded;
    } finally { lease.release(); }
  }
}
